import type { HTMLAttributes } from "react";

import { cn } from "@/lib/utils";

export type ClientViewMode = "presenter" | "visitor";

export type ClientViewModeToggleProps = {
  value: ClientViewMode;
  onChange?: (mode: ClientViewMode) => void;
} & Omit<HTMLAttributes<HTMLDivElement>, "onChange" | "children">;

const OPTIONS: readonly { id: ClientViewMode; label: string }[] = [
  { id: "presenter", label: "Presenter" },
  { id: "visitor", label: "Visitor" },
] as const;

/**
 * Presenter / Visitor view toggle (Figma 654:334).
 * Selected option uses translucent fill + cyan L-brackets; idle is plain text.
 */
export function ClientViewModeToggle({
  value,
  onChange,
  className,
  ...props
}: ClientViewModeToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="View mode"
      className={cn(
        "inline-flex flex-col items-start bg-[rgba(11,22,40,0.5)] p-1.5",
        className,
      )}
      {...props}
    >
      <div className="flex w-full items-center gap-1">
        {OPTIONS.map((option) => {
          const selected = value === option.id;

          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={option.label}
              onClick={() => onChange?.(option.id)}
              className={cn(
                "relative flex h-8 shrink-0 flex-col items-center justify-center px-5 font-['Inter'] text-xs font-medium leading-5 tracking-[-0.3px] text-white transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#38bdf8] focus-visible:ring-offset-2 focus-visible:ring-offset-black",
                selected
                  ? "min-w-[76px] border border-[rgba(56,189,248,0.35)] bg-[rgba(56,189,248,0.35)] backdrop-blur-[8px]"
                  : "bg-transparent hover:text-white/80",
              )}
            >
              {selected ? (
                <>
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-0 top-0 size-3 border-l border-t border-[#38bdf8]"
                  />
                  <span
                    aria-hidden
                    className="pointer-events-none absolute bottom-0 right-0 size-3 border-b border-r border-[#38bdf8]"
                  />
                </>
              ) : null}
              <span className="relative z-10 whitespace-nowrap">
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
